import { inject, Injectable } from '@angular/core';
import { del, get, post, put } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_CONFIG } from '../config/api-config';
import { HttpMethod } from './http-method';

// from aws-amplify package harvested
type RestApiOptions = NonNullable<Parameters<typeof get>[0]['options']>;
type AmplifyOperation = ReturnType<typeof get>;

@Injectable({
  providedIn: 'root',
})
export class BackendApiService {
  private apiConfig = inject(API_CONFIG);

  /**
   * Call public endpoints that require IAM authorization (SigV4 signing).
   * Uses Cognito Identity Pool credentials (guest or authenticated).
   */
  public async callPublicEndpoint<T, P = unknown>(endpoint: string, httpMethod: HttpMethod, payload?: P, queryParams?: Record<string, string>): Promise<T> {
    try {
      const session = await fetchAuthSession();
      if (!session.credentials) {
        throw new Error('No credentials available. Ensure Cognito Identity Pool is configured with guest access.');
      }
      const options = this.#buildOptions(payload, queryParams);
      return await this.#executeOperation<T>(this.#buildOperation(httpMethod, endpoint, options));
    } catch (error) {
      throw await this.#enrichError(error);
    }
  }

  /**
   * Call authenticated endpoints using the Cognito ID token (Bearer auth).
   */
  public async callAuth<T, P = unknown>(endpoint: string, httpMethod: HttpMethod, payload?: P, queryParams?: Record<string, string>): Promise<T> {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      const options = this.#buildOptions(payload, queryParams, { Authorization: idToken ? `Bearer ${idToken}` : '' });
      return await this.#executeOperation<T>(this.#buildOperation(httpMethod, endpoint, options));
    } catch (error) {
      throw await this.#enrichError(error);
    }
  }

  // ---------- Private helpers ----------

  #buildOptions(payload?: unknown, queryParams?: Record<string, string>, headers?: Record<string, string>): RestApiOptions {
    const options: RestApiOptions = {};
    if (headers) options.headers = headers;
    if (queryParams) options.queryParams = queryParams;
    if (payload) options.body = payload as RestApiOptions['body'];
    return options;
  }

  #buildOperation(httpMethod: HttpMethod, endpoint: string, options: RestApiOptions): AmplifyOperation {
    const apiInput = { apiName: this.apiConfig.apiName, path: endpoint, options };
    switch (httpMethod) {
      case 'GET':
        return get(apiInput);
      case 'POST':
        return post(apiInput);
      case 'PUT':
        return put(apiInput);
      case 'DELETE':
        return del(apiInput);
      default:
        throw new Error('Unsupported HTTP method: ' + httpMethod);
    }
  }

  async #executeOperation<T>(operation: AmplifyOperation): Promise<T> {
    const response = await operation.response;
    if (response.statusCode === 204) {
      return undefined as T;
    }
    const text = await response.body?.text();
    if (!text) {
      return undefined as T;
    }
    return JSON.parse(text) as T;
  }

  async #enrichError(error: unknown): Promise<unknown> {
    const serverMessage = this.#extractServerMessage(error);
    if (serverMessage) {
      (error as Record<string, unknown>)['serverMessage'] = serverMessage;
    }
    console.error('API error:', error);
    return error;
  }

  #extractServerMessage(error: unknown): string | undefined {
    const body = (error as { response?: { body?: unknown } })?.response?.body;
    if (!body) return undefined;

    // Amplify v6 REST errors: body is a string containing JSON
    if (typeof body === 'string') {
      try {
        return (JSON.parse(body) as { error?: string }).error;
      } catch {
        return undefined;
      }
    }

    // Body is already a parsed object
    if (typeof body === 'object' && 'error' in body) {
      const msg = (body as { error?: unknown }).error;
      return typeof msg === 'string' ? msg : undefined;
    }

    return undefined;
  }
}
